/**
 * Chart series descriptors for the stacked bar / area / line charts.
 *
 * A taxon name may contain any printable character — open-nomenclature qualifiers (`sp.`, `cf.`,
 * `aff.`) end in a period — so a name embedded in a delimited key string cannot be recovered from
 * it. `id` is therefore positional and carries no name, `label` is the name itself, and `value`
 * reads the datum by closure. No delimiter, so no delimiter is safe or unsafe.
 */
export type StackSeries = {
  /** Series identity for chart-library keying. Positional; never derived from `label`. */
  id: string
  /** The taxon (or trait group) this series represents: display label and click-through value. */
  label: string
  value: (row: unknown) => number
}

const numberAt = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0)

const asRecord = (v: unknown): Record<string, unknown> | undefined =>
  typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : undefined

/** Rows shaped `{ data: { [label]: number } }`. */
export const nestedStackSeries = (labels: readonly string[]): StackSeries[] =>
  labels.map((label, i) => ({
    id: `s${i}`,
    label,
    value: (row) => numberAt(asRecord(asRecord(row)?.['data'])?.[label])
  }))

/** Rows shaped `{ data: { [label]: { [metric]: number } } }`. */
export const nestedMetricStackSeries = (labels: readonly string[], metric: string): StackSeries[] =>
  labels.map((label, i) => ({
    id: `s${i}`,
    label,
    value: (row) => numberAt(asRecord(asRecord(asRecord(row)?.['data'])?.[label])?.[metric])
  }))

/** Rows shaped `{ [label]: number }`. */
export const flatStackSeries = (labels: readonly string[]): StackSeries[] =>
  labels.map((label, i) => ({
    id: `s${i}`,
    label,
    value: (row) => numberAt(asRecord(row)?.[label])
  }))
